import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DEPARTMENTS } from "@/lib/departments";
import MapOverview from "@/components/MapOverview";
import { toast } from "sonner";
import { MapPin, Calendar, HandHeart, ChatCircleDots, Heart, Lightbulb, GridFour, MapTrifold } from "@phosphor-icons/react";

function ClaimDialog({ report, onClaimed }) {
  const [open, setOpen] = useState(false);
  const [myDoudous, setMyDoudous] = useState([]);
  const [chosen, setChosen] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/doudous");
      const lost = data.filter((d) => d.status === "lost");
      setMyDoudous(lost.length ? lost : data);
      if (lost.length === 1) setChosen(lost[0].doudou_id);
    })();
  }, [open]);

  const submit = async () => {
    if (!chosen) { toast.error("Choisissez le doudou concerné"); return; }
    if (message.trim().length < 20) { toast.error("Détaillez un peu votre message (20+ caractères)"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/found-reports/${report.report_id}/claim`, {
        doudou_id: chosen,
        initial_message: message.trim(),
      });
      toast.success("Message envoyé à la personne qui l'a trouvé !");
      onClaimed && onClaimed(data.conversation_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  const selectedDoudou = myDoudous.find((d) => d.doudou_id === chosen);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-orange-500 hover:bg-orange-600 rounded-full font-bold" data-testid={`claim-button-${report.report_id}`}>
          <HandHeart size={18} weight="bold" className="mr-2" /> C'est mon doudou !
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Contacter {report.finder_first_name}</DialogTitle>
        </DialogHeader>

        {myDoudous.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-stone-600">Vous n'avez pas encore enregistré de doudou.</p>
            <Link to="/doudou/nouveau"><Button className="mt-4 bg-orange-500 hover:bg-orange-600 rounded-full">Créer une fiche doudou</Button></Link>
          </div>
        ) : (
          <>
            <div>
              <Label>Lequel de vos doudous pensez-vous que c'est ?</Label>
              <div className="grid grid-cols-2 gap-3 mt-2 max-h-48 overflow-y-auto" data-testid="claim-doudou-list">
                {myDoudous.map((d) => (
                  <button
                    key={d.doudou_id}
                    onClick={() => setChosen(d.doudou_id)}
                    className={`text-left rounded-2xl border-2 p-3 transition ${chosen === d.doudou_id ? "border-orange-500 bg-orange-50" : "border-rose-100 bg-white"}`}
                    data-testid={`claim-doudou-${d.doudou_id}`}
                  >
                    <div className="aspect-square rounded-xl bg-rose-100 overflow-hidden mb-2">
                      {d.photo ? <img src={d.photo} alt={d.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center"><Heart size={32} weight="duotone" className="text-rose-300" /></div>}
                    </div>
                    <p className="font-display font-bold text-sm">{d.name}</p>
                    {d.status === "lost" ? <span className="text-xs text-rose-600 font-bold">Perdu</span> : <span className="text-xs text-stone-400">À la maison</span>}
                  </button>
                ))}
              </div>
            </div>

            {selectedDoudou?.secret_detail && (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 text-sm">
                <p className="font-bold text-stone-700 flex items-center gap-1"><Lightbulb size={16} weight="duotone" className="text-rose-500" /> Aide-mémoire (facultatif)</p>
                <p className="text-stone-600 italic mt-1">« {selectedDoudou.secret_detail} »</p>
                <p className="text-xs text-stone-500 mt-1">Vous pouvez glisser ce détail dans votre message pour vérifier — l'échange continue dans tous les cas, c'est juste une aide.</p>
              </div>
            )}

            <div>
              <Label htmlFor="claim-msg">Votre premier message *</Label>
              <Textarea
                id="claim-msg"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Bonjour, je pense que c'est mon doudou. Pouvez-vous me dire s'il a [détail spécifique] ? Merci beaucoup !"
                className="rounded-2xl mt-1"
                data-testid="claim-message-input"
              />
              <p className="text-xs text-stone-400 mt-1">Conseil : posez une question sur un détail unique pour confirmer.</p>
            </div>

            <Button onClick={submit} disabled={sending} className="bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold" data-testid="claim-submit-button">
              {sending ? "Envoi..." : "Envoyer le message"}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FoundReports() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [myLost, setMyLost] = useState([]);
  const [publicLost, setPublicLost] = useState([]);
  const [view, setView] = useState("grid");  // grid | map
  const [fetching, setFetching] = useState(true);

  // Unified dept filter, synced with URL ?cp=XX (2-digit dept code, e.g. "31").
  // Anonymous AND inscribed users share the exact same UI — full national map by
  // default, with an optional dropdown or postal-code input to focus a single dept.
  const dept = searchParams.get("cp") || "";
  const [postalInput, setPostalInput] = useState("");
  const setDept = (value) => {
    if (value) setSearchParams({ cp: value });
    else setSearchParams({});
  };

  // Reset the postal input if the URL is cleared (e.g. user clicks "Toute la France").
  useEffect(() => {
    if (!dept) setPostalInput("");
  }, [dept]);

  // Inscribed users — private endpoint (their own lost doudous + all found)
  useEffect(() => {
    if (loading || !user) return;
    (async () => {
      setFetching(true);
      try {
        const [reports, mine] = await Promise.all([
          api.get("/found-reports", { params: { dept: dept || undefined } }),
          api.get("/doudous"),
        ]);
        setItems(reports.data);
        setMyLost(mine.data.filter((d) => d.status === "lost" && d.lost_location));
      } catch (e) {
        toast.error("Impossible de charger les signalements");
      } finally {
        setFetching(false);
      }
    })();
  }, [user, loading, dept]);

  // Anonymous visitors — public map markers (nationwide by default, filtered on demand)
  useEffect(() => {
    if (loading || user) return;
    (async () => {
      setFetching(true);
      try {
        const { data } = await api.get("/public/map-markers", {
          params: dept ? { department: dept } : {},
        });
        // Convert found markers into card-like items reusing the same fields
        const found = (data.found || []).map((m) => ({
          report_id: m.id,
          photo: m.photo,
          description: m.title,
          location_label: m.subtitle?.split(" · par ")?.[0] || "",
          finder_first_name: m.subtitle?.split(" · par ")?.[1] || "",
          created_at: new Date().toISOString(),
          status: "open",
          lat: m.lat, lng: m.lng,
          public_marker: true,
        }));
        setItems(found);
        setPublicLost(data.lost || []);
      } catch {
        setItems([]); setPublicLost([]);
      }
      setFetching(false);
    })();
  }, [user, loading, dept]);

  const submitPostal = (e) => {
    e?.preventDefault?.();
    const cp = postalInput.replace(/\s/g, "");
    if (!/^\d{5}$/.test(cp)) { toast.error("Code postal invalide (5 chiffres)"); return; }
    setDept(cp.slice(0, 2));
  };

  const mapItems = user
    ? [
        ...items.filter((r) => r.lat && r.lng).map((r) => ({
          id: r.report_id,
          lat: r.lat, lng: r.lng,
          type: "found",
          title: r.description?.slice(0, 60) || "Doudou trouvé",
          subtitle: `${r.location_label || ""} · par ${r.finder_first_name}`,
          photo: r.photo,
        })),
        ...myLost.map((d) => ({
          id: d.doudou_id,
          lat: d.lost_location.lat, lng: d.lost_location.lng,
          type: "lost",
          title: d.name,
          subtitle: "Votre doudou perdu",
          link: `/doudou/${d.doudou_id}`,
          photo: d.photo,
        })),
      ]
    : [
        ...items.map((r) => ({
          id: r.report_id,
          lat: r.lat, lng: r.lng,
          type: "found",
          title: r.description?.slice(0, 60) || "Doudou trouvé",
          subtitle: `${r.location_label || ""} · par ${r.finder_first_name}`,
          photo: r.photo,
          // link omitted -> popup shows "Inscription gratuite" CTA
        })),
        ...publicLost.map((m) => ({
          id: m.id,
          lat: m.lat, lng: m.lng,
          type: "lost",
          title: m.title,
          subtitle: m.subtitle,
          link: m.link, // /alerte/<id> — public
          photo: m.photo,
        })),
      ];

  const isAnonymous = !user;
  const activeDept = dept;
  const activeDeptLabel = dept
    ? (DEPARTMENTS.find((d) => d.code === dept)?.name || `Dép. ${dept}`)
    : "Toute la France";

  return (
    <Layout>
      <SEO
        title="Carte des doudous signalés près de chez moi"
        description={
          dept
            ? `Doudous perdus et trouvés récemment dans le département ${dept}`
            : "Carte des doudous signalés (perdus et trouvés) partout en France."
        }
      />
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">
            {dept ? `Département ${dept} · ${activeDeptLabel}` : "Toute la France"}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-stone-800 mt-2" data-testid="found-reports-title">
            Carte des doudous signalés
          </h1>
          <p className="text-stone-600 mt-3 max-w-2xl mx-auto">
            {isAnonymous
              ? "Parcourez les doudous perdus et trouvés partout en France. Vous reconnaissez le vôtre ? Créez un compte gratuit pour discuter avec la personne qui l'a trouvé."
              : "Parcourez les doudous signalés par des personnes qui les ont retrouvés. Si vous reconnaissez le vôtre, lancez une conversation avec une question précise pour vérifier."}
          </p>
        </div>

        {/* Unified toolbar: postal input (above) + department dropdown (below) + view toggle */}
        <div className="bg-white rounded-3xl border border-rose-100 p-5 sm:p-6 mb-8 space-y-4" data-testid="found-reports-toolbar">
          {/* Postal code shortcut */}
          <form onSubmit={submitPostal} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-testid="postal-code-form">
            <Label className="text-stone-700 font-bold sm:w-40 shrink-0">
              Votre code postal
            </Label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              placeholder="Ex. 31000"
              value={postalInput}
              onChange={(e) => setPostalInput(e.target.value)}
              className="flex-1 rounded-full border-2 border-rose-100 px-5 py-2 text-lg font-bold tracking-widest text-center focus:outline-none focus:border-orange-400"
              data-testid="postal-code-input"
            />
            <Button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600 rounded-full px-6 font-bold shadow shadow-orange-500/20"
              data-testid="postal-code-submit"
            >
              Voir ce département
            </Button>
          </form>

          <div className="h-px bg-rose-50" />

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Label className="text-stone-700 font-bold sm:w-40 shrink-0">
                Ou choisir un département
              </Label>
              <select
                value={dept}
                onChange={(e) => setDept(e.target.value)}
                className="rounded-full border-2 border-rose-100 px-4 py-2 bg-white font-semibold"
                data-testid="found-reports-dept-filter"
              >
                <option value="">Toute la France</option>
                {DEPARTMENTS.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
              </select>
              {dept && (
                <button
                  type="button"
                  onClick={() => { setDept(""); setPostalInput(""); }}
                  className="text-sm font-bold text-orange-600 hover:underline text-left"
                  data-testid="clear-dept-filter"
                >
                  ← Voir toute la France
                </button>
              )}
            </div>

            <div className="flex gap-1 bg-rose-50 rounded-full p-1" data-testid="found-reports-view-toggle">
              <button onClick={() => setView("grid")} className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 transition ${view === "grid" ? "bg-white text-orange-600 shadow" : "text-stone-500"}`} data-testid="view-grid-button">
                <GridFour size={18} weight="duotone" /> Grille
              </button>
              <button onClick={() => setView("map")} className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 transition ${view === "map" ? "bg-white text-orange-600 shadow" : "text-stone-500"}`} data-testid="view-map-button">
                <MapTrifold size={18} weight="duotone" /> Carte
              </button>
            </div>
          </div>

          {isAnonymous && (
            <p className="text-xs text-stone-500 text-center pt-2 border-t border-rose-50">
              Vous reconnaissez un doudou ? <Link to="/inscription" className="text-orange-600 font-bold" data-testid="public-register-cta">Créez un compte gratuit</Link> pour discuter avec la personne qui l'a trouvé.
            </p>
          )}
        </div>

        {view === "map" && (
          <div className="mb-6">
            {mapItems.length === 0 ? (
              <div className="bg-white rounded-3xl border border-rose-100 p-12 text-center">
                <MapTrifold size={56} weight="duotone" className="mx-auto text-rose-300" />
                <p className="mt-4 text-stone-500">Aucun marqueur à afficher pour ce filtre.</p>
              </div>
            ) : (
              <MapOverview items={mapItems} height={520} />
            )}
            <p className="text-xs text-stone-500 mt-3 text-center">
              {isAnonymous
                ? "Cliquez sur un marqueur pour voir un doudou. Inscription gratuite pour contacter la personne qui l'a trouvé."
                : "Cliquez sur un marqueur pour voir le détail. Vos doudous perdus apparaissent en rose, les signalements trouvés en orange."}
            </p>
          </div>
        )}

        {view === "grid" && (
          <>
        {fetching ? (
          <p className="text-stone-500 text-center py-12">Chargement...</p>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-rose-100 p-12 text-center" data-testid="found-reports-empty">
            <Heart size={56} weight="duotone" className="mx-auto text-rose-300" />
            <h3 className="font-display text-xl font-bold text-stone-800 mt-4">Aucun signalement {activeDept ? `dans le ${activeDept}` : "pour le moment"}</h3>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((r) => (
              <div key={r.report_id} className="bg-white rounded-3xl border border-rose-100 overflow-hidden" data-testid={`found-report-${r.report_id}`}>
                <div className="aspect-square bg-gradient-to-br from-rose-100 to-orange-100 grid place-items-center overflow-hidden">
                  {r.photo ? <img src={r.photo} alt="doudou trouvé" className="w-full h-full object-contain" /> : <Heart size={80} weight="duotone" className="text-rose-300" />}
                </div>
                <div className="p-5">
                  <p className="text-sm text-stone-500 flex items-center gap-1"><Calendar size={14} /> {new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
                  <p className="font-display font-bold text-stone-800 mt-1 line-clamp-2">{r.description}</p>
                  {r.location_label && (
                    <p className="text-sm text-stone-500 mt-2 flex items-center gap-1"><MapPin size={14} /> {r.location_label}</p>
                  )}
                  {r.finder_first_name && <p className="text-xs text-stone-400 mt-1">Trouvé par {r.finder_first_name}</p>}
                  <div className="mt-4">
                    {isAnonymous ? (
                      <Link to="/inscription" data-testid={`public-register-claim-${r.report_id}`}>
                        <Button className="w-full bg-rose-500 hover:bg-rose-600 rounded-full font-bold">
                          C'est mon doudou — Inscription gratuite
                        </Button>
                      </Link>
                    ) : (
                      <>
                        {r.status === "claimed" && (
                          <p className="text-xs text-orange-700 bg-orange-50 rounded-full px-3 py-1.5 mb-2 text-center font-bold">
                            Une famille est déjà en discussion, mais si vous le reconnaissez aussi, cliquez ci-dessous.
                          </p>
                        )}
                        <ClaimDialog report={r} onClaimed={(convId) => navigate(`/conversations/${convId}`)} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </div>
    </Layout>
  );
}
